import React from 'react';
import { useNavigate } from 'react-router-dom';

export default function Dashboard(){
  return (
    <div className="space-y-6">
      <WelcomeHeader />
      <KpiRow />
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <ChartsPanel />
        <ActivityFeed />
      </div>
    </div>
  );
}

function WelcomeHeader(){
  const navigate = useNavigate();
  return (
    <div className="rounded-2xl bg-gradient-to-br from-white to-slate-50 border border-slate-200 p-6 shadow-sm">
      <h1 className="text-2xl font-bold tracking-tight">Welcome to your Customized K‑R‑O Experience</h1>
      <p className="text-slate-600 mt-1 text-sm">Start with Trust Signal insight, monitor supplier ecosystems, and run the Trust Tech Scan to reveal optimization opportunities.</p>
      <div className="mt-4 flex gap-2">
  <button onClick={()=> navigate('/trust-tech-scan/run')} className="px-4 py-2 rounded-lg bg-purple-700 text-white hover:bg-purple-800">Run Trust Tech Scan</button>
  <button onClick={()=> navigate('/reports/create')} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200">Create Report</button>
      </div>
    </div>
  );
}

function KpiRow(){
  const cards = [
    { label:'Trust Signals (30d)', value:128, delta:+12 },
    { label:'Active Suppliers', value:342, delta:+8 },
    { label:'ESG Issues Flagged', value:17, delta:-3 },
    { label:'Optimizations Running', value:9, delta:+2 }
  ];
  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {cards.map((c,i)=>(
        <div key={i} className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
          <div className="text-slate-500 text-xs">{c.label}</div>
            <div className="mt-1 text-2xl font-extrabold">{c.value}</div>
            <div className={`text-xs mt-1 ${c.delta>=0?'text-emerald-600':'text-rose-600'}`}>{c.delta>=0?'▲':'▼'} {Math.abs(c.delta)}%</div>
        </div>
      ))}
    </section>
  );
}

function ChartsPanel(){
  return (
    <section className="xl:col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ChartCard title="ESG Score Trend (90d)" subtitle="Composite index across E/S/G"><MiniLine /></ChartCard>
      <ChartCard title="Supplier Risk Radar" subtitle="Exposure by category"><MiniRadar /></ChartCard>
      <ChartCard title="Global Supplier Footprint" subtitle="Top regions by count"><MiniBars /></ChartCard>
      <ChartCard title="Trust Signal Types" subtitle="Proportion of signals last 30d"><MiniDonut /></ChartCard>
    </section>
  );
}

function ChartCard({title, subtitle, children}){
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
      <div className="mb-2">
        <div className="font-semibold">{title}</div>
        {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
      </div>
      <div className="h-48 grid place-items-center">{children}</div>
    </div>
  );
}

function ActivityFeed(){
  const items = [
    { t:'10:12', title:'Supplier onboarding completed', meta:'Green Leaf Co.' },
    { t:'09:47', title:'Trust Signal: data discrepancy', meta:'Auto‑resolved • Severity: low' },
    { t:'Yesterday', title:'Quarterly ESG report exported (PDF)', meta:'by Alex Morgan' },
    { t:'Mon', title:'Policy update applied', meta:'Supplier code of conduct v2.1' }
  ];
  return (
    <section className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
      <div className="font-semibold mb-2">Recent Activity</div>
      <ol className="relative border-s border-slate-200 pl-4">
        {items.map((it,i)=>(
          <li key={i} className="mb-4">
            <div className="absolute -left-2 w-3 h-3 rounded-full bg-purple-600" />
            <div className="text-xs text-slate-500">{it.t}</div>
            <div className="font-medium leading-tight">{it.title}</div>
            <div className="text-xs text-slate-500">{it.meta}</div>
          </li>
        ))}
      </ol>
      <button className="mt-2 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 border border-slate-200 text-sm">View all</button>
    </section>
  );
}

// Mini charts reused
function MiniLine(){
  const points = [0,20,35,25,50,45,60,55,70,65,85,80,100];
  const path = points.map((y,i)=> `${i===0?'M':'L'} ${i*24} ${100 - y}`).join(' ');
  return (
    <svg viewBox="0 0 288 100" className="w-full h-full">
      <defs>
        <linearGradient id="glow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#7c3aed" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={path} fill="none" stroke="#7c3aed" strokeWidth="3" />
      <path d={`${path} L 288 100 L 0 100 Z`} fill="url(#glow)" />
    </svg>
  );
}
function MiniBars(){
  const vals = [24,38,31,18,12];
  const max = Math.max(...vals);
  return (
    <svg viewBox="0 0 200 100" className="w-full h-full">
      {vals.map((v,i)=>{ const h=(v/max)*90; return <rect key={i} x={i*36+10} y={100-h} width="24" height={h} rx="4" fill="#7c3aed" />})}
    </svg>
  );
}
function MiniDonut(){
  const slices = [28,22,30,20];
  const colors = ['#7c3aed','#a78bfa','#22c55e','#f59e0b'];
  let acc=0; const arcs = slices.map((s,i)=>{ const start=(acc/100)*2*Math.PI; acc+=s; const end=(acc/100)*2*Math.PI; const large=s>50?1:0; const r=36,cx=50,cy=50; const x1=cx+r*Math.cos(start), y1=cy+r*Math.sin(start); const x2=cx+r*Math.cos(end), y2=cy+r*Math.sin(end); return <path key={i} d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`} fill={colors[i]} />;});
  return (
    <svg viewBox="0 0 100 100" className="w-40 h-40">
      {arcs}
      <circle cx="50" cy="50" r="22" fill="white" />
      <text x="50" y="54" textAnchor="middle" className="fill-slate-700 text-sm">30d</text>
    </svg>
  );
}
function MiniRadar(){
  const spokes=5; const r=42,cx=60,cy=52; const vals=[0.7,0.55,0.85,0.4,0.65];
  const poly = vals.map((v,i)=>{ const a=(i/spokes)*2*Math.PI - Math.PI/2; return `${cx + r*v*Math.cos(a)},${cy + r*v*Math.sin(a)}`;}).join(' ');
  const grid=[...Array(spokes)].map((_,i)=>{ const a=(i/spokes)*2*Math.PI - Math.PI/2; return <line key={i} x1={cx} y1={cy} x2={cx + r*Math.cos(a)} y2={cy + r*Math.sin(a)} stroke="#e2e8f0" />;});
  return (
    <svg viewBox="0 0 120 104" className="w-44 h-40">
      <circle cx={cx} cy={cy} r={r} fill="#f8fafc" stroke="#e2e8f0" />
      {grid}
      <polygon points={poly} fill="#7c3aed33" stroke="#7c3aed" />
    </svg>
  );
}
